import {
  BrandTypes,
  OpenURLMode,
  ProductTermUnitTypes,
  YXH_APP_PROTOCOL,
  YXH_DOMAINS,
  CouponSourceTypes,
  CouponTypes,
  YXH_OPEN_CUSTOM_DOMAIN,
} from '@/shared/constants';
import { detectEnv } from '@/shared/detect';
import yxhJsBridge from './yxhJsBridge';
import BigNumber from 'bignumber.js';
import type { Route } from 'vue-router';
import { parseQuery, stringifyQuery } from './query';
import { isString, trim } from 'lodash';
import { Toast } from 'vant';
import importScript from '@/shared/importScript';
import simpleTween from '@/shared/simpleTween';
import { nextTick, onMounted, onActivated } from 'vue';
import LaunchApp from './launchApp';
import formatDiscount from '@/shared/filters/formatDiscount';
import formatAmount from '@/shared/filters/formatAmount';
import dayjs from 'dayjs';

/**
 * 去除字符串中全部空格
 */
export function trimAll(str: string): string {
  return str.replace(/\s+/g, '');
}

/**
 * 字符串转数值
 */
export function string2number(str: string | number): number {
  if (typeof str === 'number') return str;
  const value = Number.parseFloat(str);

  if (Number.isNaN(value)) {
    return 0;
  }

  return value;
}

/**
 * 判断是否为Promise对象
 */
export function isPromise<T>(value: unknown): value is Promise<T> {
  return (
    value != null &&
    (typeof value === 'object' || typeof value === 'function') &&
    typeof (value as Promise<T>).then === 'function' &&
    typeof (value as Promise<T>).catch === 'function'
  );
}

/**
 * 判断路径是否为宜享花域名
 * @param url
 */
export function isYxhDomain(url: string) {
  const [, domain] = url.match(/^(?:https?:)?(?:\/\/)?([\w.-]+)\S*$/) ?? [];

  if (domain) {
    return YXH_DOMAINS.includes(domain);
  }

  return false;
}

/**
 * 获取当前域名
 */
export function getCurrentDomain() {
  const { origin } = window.location;

  return `${origin}${process.env.VUE_APP_PREFIX}`;
}

interface OptionExternalUrlOptions {
  url: string;
  mode?: OpenURLMode;
  showTitle?: boolean;
  whenCloseToRoot?: boolean;
  replace?: boolean;
}

// 直接使用localStorage的场景是因为该功能与业务无关，正常情况下业务相关的数据依然需要存储在pinia中
const OPEN_EXTERNAL_PAGE_START_HISTORY_LENGTH_KEY = 'OPEN_EXTERNAL_PAGE_START_HISTORY_LENGTH';
/**
 * 打开外部网址
 */
export function openExternalUrl(options: OptionExternalUrlOptions | string) {
  let opts = options as OptionExternalUrlOptions;

  if (isString(options)) {
    opts = {
      url: options,
    };
  }

  opts.mode = opts.mode ?? OpenURLMode.WEBVIEW;
  opts.showTitle = opts.showTitle ?? true;
  opts.whenCloseToRoot = opts.whenCloseToRoot ?? false;
  opts.replace = opts.replace ?? false;

  if (detectEnv.yxh) {
    if (opts.mode === OpenURLMode.BROWSER) {
      yxhJsBridge.openBrowser(opts.url);
    } else {
      yxhJsBridge.openWebView({
        url: opts.url,
        showTitle: opts.showTitle,
        whenCloseToRoot: opts.whenCloseToRoot,
      });
    }
  } else {
    // 跳转外部URL之前总是记录当时的window.history.length
    localStorage.setItem(
      OPEN_EXTERNAL_PAGE_START_HISTORY_LENGTH_KEY,
      String(window.history.length),
    );

    if (opts.replace) {
      // 宜享花APP外使用，可以忽略该eslint规则
      // eslint-disable-next-line no-restricted-syntax
      window.location.replace(opts.url);
    } else {
      // 宜享花APP外使用，可以忽略该eslint规则
      // eslint-disable-next-line no-restricted-syntax
      window.location.href = opts.url;
    }
  }
}

/**
 * 微信小程序环境已就绪
 */
export function weappReady(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof wx !== 'undefined' && wx.miniProgram) {
      resolve();
      return;
    }

    importScript(`${process.env.BASE_URL}sdk/jweixin-1.6.0.js`, {
      retry: true,
    })
      .then(() => {
        resolve();
      })
      .catch(reject);
  });
}

/**
 * 抖音小程序环境已就绪
 */
export function dyappReady(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof tt !== 'undefined' && tt.miniProgram) {
      resolve();
      return;
    }

    importScript(`${process.env.BASE_URL}sdk/douyin-jssdk-1.2.1.js`, {
      retry: true,
    })
      .then(() => {
        resolve();
      })
      .catch(reject);
  });
}

/**
 * 后退到调用打开外部链接时的页面，配合openExternalUrl使用
 */
export function backToBeforeOpenExternalUrl() {
  // 宜享花APP
  if (detectEnv.yxh) {
    yxhJsBridge.close();

    // 抖音小程序
  } else if (detectEnv.dyapp) {
    dyappReady().then(() => {
      tt.miniProgram.navigateBack();
    });
    // 其他场景
  } else {
    const startHistoryLength = localStorage.getItem(OPEN_EXTERNAL_PAGE_START_HISTORY_LENGTH_KEY);

    localStorage.removeItem(OPEN_EXTERNAL_PAGE_START_HISTORY_LENGTH_KEY);

    if (startHistoryLength && Number.isFinite(Number(startHistoryLength))) {
      window.history.go(Math.min(Number(startHistoryLength) - window.history.length, -1));
    } else {
      window.history.back();
    }
  }
}

export interface OpenNativeViewOptions {
  params?: Record<string, any>;
  deeplink?: boolean;
  fallback?: string;
}

/**
 * 打开宜享花原生页面
 */
export function openNativeView(url: string, options?: OpenNativeViewOptions) {
  // 在非宜享花APP环境下如果没开启DeppLink则提示用户去宜享花APP内访问
  if (!detectEnv.yxh && !options?.deeplink) {
    Toast.fail('请在宜享花APP中访问');
    return;
  }

  const [p, s] = url.split('?');
  const pathname = p.startsWith(YXH_APP_PROTOCOL) ? p.slice(YXH_APP_PROTOCOL.length) : p;
  let search = stringifyQuery({
    ...parseQuery(s),
    ...options?.params,
  });

  if (search.length > 0) {
    search = `?${search}`;
  }

  const fullPath = `${trim(pathname, '/')}${search}`;
  // 在非宜享花环境下如果开启了DeppLink则通过URL Scheme 打开宜享花App
  if (!detectEnv.yxh && options?.deeplink) {
    new LaunchApp({
      protocol: 'yixianghua',
      fallback: options.fallback ?? `${getCurrentDomain()}misc/download-app`,
    }).open(fullPath);
    return;
  }

  // 专门用于打开宜享花原生页面，可以忽略该eslint规则
  // eslint-disable-next-line no-restricted-syntax
  window.location.href = `${YXH_APP_PROTOCOL}${fullPath}`;
}

/**
 * 获取指定元素的scrollTop
 */
export function getScrollTop(el: Element | Window) {
  const top = 'scrollTop' in el ? el.scrollTop : el.scrollY;

  return Math.max(0, top);
}

/**
 * 获取指定元素到页面顶部的距离
 */
export function getOffsetTop(el: HTMLElement | null) {
  if (!el) {
    return 0;
  }

  const win = el.ownerDocument.defaultView;

  return Math.round(el.getBoundingClientRect().top + (win?.pageYOffset ?? 0));
}

/**
 * 获取当前滚动的位置
 */
export function getCurrentScrollTop() {
  return Math.round(document.documentElement.scrollTop);
}

/**
 * 滚动到指定位置
 */
export function scrollTo(end: number, onComplete?: () => void, duration = 400) {
  const scrollTop = getCurrentScrollTop();

  simpleTween({
    start: scrollTop,
    end,
    duration,
    onUpdate(v) {
      window.scrollTo(0, v);
    },
    onComplete,
  });
}

/**
 * vw/vh单位转换像素
 */
export function unit2pixel(value: number | string) {
  if (typeof value === 'number') {
    return value;
  }

  if (value.indexOf('vw') !== -1) {
    return new BigNumber(+value.replace(/vw/g, ''))
      .times(window.innerWidth)
      .div(100)
      .integerValue(BigNumber.ROUND_FLOOR)
      .toNumber();
  }

  if (value.indexOf('vh') !== -1) {
    return new BigNumber(+value.replace(/vh/g, ''))
      .times(window.innerHeight)
      .div(100)
      .integerValue(BigNumber.ROUND_FLOOR)
      .toNumber();
  }

  return parseFloat(value);
}

/**
 * 获取期数文本
 */
export function getTermText(term: number | string, unit: ProductTermUnitTypes) {
  if (term == null) return '';

  switch (unit) {
    case ProductTermUnitTypes.DAY:
      // 一期30天
      return Number(term) * 30 + '天';
    case ProductTermUnitTypes.PERIOD:
      return `${term}个月`;
    default:
      return `${term}个月`;
  }
}

/**
 * 判断是否为H5主页
 */
export function isHomePath(p: string) {
  return p === '/' || p === '/home';
}

/**
 * 判断是否为放款结果页
 */
export function isPartnerLoanResultPath(p: string) {
  return p === '/' || p === '/partner/loan/result';
}

/**
 * 判断是否为提额结果页
 */
export function isPartnerCreditLimitResultPath(p: string) {
  return p === '/' || p === '/partner/credit-limit/result';
}

/**
 * 判断是否为H5钱包主页
 */
export function isWalletPath(p: string) {
  return p === '/' || p === '/wallet';
}

/**
 * 判断是否为Loading页面
 */
export function isFundWaitingPath(p: string) {
  return p === '/' || p === '/loan/fund/waiting';
}

/**
 * 判断是否为拒必退页面
 */
export function isJbtPath(p: string) {
  return p === '/' || p === '/goods/compensate-card';
}

/**
 * 判断是否为还款页
 */
export function isRepaymentPath(p: string) {
  return p === '/' || p === '/repayment/bill';
}

/**
 * 判断是否逾期影响页面
 */
export function isOverdueIntroPath(p: string) {
  return p === '/misc/overdue-intro';
}

/**
 * 判断是否为广告主绑卡页面
 */
export function isPartnerBankCardPath(p: string) {
  return p === '/partner/bankcard';
}

/**
 * 判断是否为还款结果页
 */
export function isRepaymentResultPath(p: string) {
  return p === '/repayment/result';
}

/**
 * 判断是否为权益订单详情页
 */
export function isOrderDetailPath(p: string) {
  return p.startsWith('/order/detail/');
}

/**
 * 设置iframe跳转方式为顶部窗口
 */
export function setJumpToTopWindow(iframe: HTMLIFrameElement) {
  if (iframe == null || iframe.contentWindow == null) {
    return;
  }

  const doc = iframe.contentWindow.document;
  const a = doc.querySelectorAll('a');

  if (a) {
    for (let i = 0, item; (item = a[i++]);) {
      if (item.href !== null && item.href !== '') {
        item.target = '_top';
      }
    }
  }
}

/**
 * 判断地址是否为宜享花原生页面
 */
export function isNativeView(url: string) {
  return url.startsWith(YXH_APP_PROTOCOL);
}

/**
 * 文件数据类型
 */
export const enum FileDataTypes {
  ARRAY_BUFFER = 1,
  BINARY_STRING = 2,
  DATA_URL = 3,
  TEXT = 4,
}

/**
 * 文件转换成指定数据类型
 */
export function fileToDataType(
  file: File,
  dataType: FileDataTypes = FileDataTypes.DATA_URL,
): Promise<string | ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const data = e.target?.result;

      if (data) {
        resolve(data);
      } else {
        reject(new Error('文件解析失败，请重新选择文件'));
      }
    };

    reader.onerror = () => {
      reject(new Error('文件解析失败，请重新选择文件'));
    };

    switch (dataType) {
      case FileDataTypes.ARRAY_BUFFER:
        reader.readAsArrayBuffer(file);
        break;
      case FileDataTypes.BINARY_STRING:
        reader.readAsBinaryString(file);
        break;
      case FileDataTypes.DATA_URL:
        reader.readAsDataURL(file);
        break;
      case FileDataTypes.TEXT:
        reader.readAsText(file);
        break;
    }
  });
}

/**
 * 生成一个随机数字
 */
export function random(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * 跳转腾讯蹊径落地页
 * 参考文档：
 * 1、https://doc.weixin.qq.com/doc/w3_AIoAeAbdAFwCN2CE9WI64SFiZvS6c?scode=AJEAIQdfAAo4Nabk9uAV0AGAZ2AEQ
 * 2、https://doc.weixin.qq.com/doc/w3_AHcAVQYgACo614ewf6dSJuRaJgZpV?scode=AJEAIQdfAAofYuXyXRAaMAIAaEANA
 */
export async function toTencentXjLandingPage(qz_gdt: string, gdt_vid: string, fail?: () => void) {
  if (!window.AmsAppApi) {
    await Promise.all([
      importScript('https://i.gtimg.cn/ams-web/public/mobile-market/dist/api.js'),
    ]);
  }
  window.AmsAppApi.invokeLandingPage({
    iosAppUrl:
      'https://h5.gdt.qq.com/xjviewer/nemo/6383599?qz_gdt=' + qz_gdt + '&gdt_vid=' + gdt_vid,
    androidAppUrl:
      'https://h5.gdt.qq.com/xjviewer/nemo/6378394?qz_gdt=' + qz_gdt + '&gdt_vid=' + gdt_vid,
    callback: (res: any) => {
      if (res && res.type === 'nothing') {
        fail && fail();
      }
    },
  });
}

/**
 * 判断是否存在navbar, 自定义navbar不在范围内
 */
export function existsNavBar(route: Route) {
  // 页面地址中设置了hasNavBar为0
  if (route.query.hasNavBar === '0') {
    return false;
  }

  // 判断当前路由是否嵌套在包含了nav-bar的布局中
  const nestedNavBarLayout = route.matched?.find((item) => item.meta.hasNavBarLayout);

  if (!nestedNavBarLayout) {
    return false;
  }

  return true;
}

/**
 * 判断元素是否隐藏
 */
export function isElementHidden(el: HTMLElement) {
  const style = window.getComputedStyle(el);
  const hidden = style.display === 'none';
  // offsetParent returns null in the following situations:
  // 1. The element or its parent element has the display property set to none.
  // 2. The element has the position property set to fixed
  const parentHidden = el.offsetParent === null && style.position !== 'fixed';

  return hidden || parentHidden;
}

/**
 * 获取滚动元素父级
 */
const overflowScrollRegex = /scroll|auto|overlay/i;
export function getScrollParent(el?: HTMLElement | null, root: HTMLElement | Window = window) {
  let node = el;

  while (
    node &&
    node.tagName !== 'HTML' &&
    node.tagName !== 'BODY' &&
    node.nodeType === 1 &&
    node !== root
  ) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowScrollRegex.test(overflowY)) {
      return node;
    }

    node = node.parentNode as HTMLElement;
  }

  return root;
}

/**
 * 添加px单位
 */
export function addUnit(value?: string | number) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `${value}px`;
  }

  return value;
}

/**
 * 获取页面标题
 */
export function getTitle(route: Route): string {
  if (route.meta?.title) {
    return route.meta.title;
  }

  return (route.query.brand as string)?.toUpperCase() === 'YRXD' ? BrandTypes.YRXD : BrandTypes.YXH;
}

/**
 * 判断是否为有效数组
 */
export function isValidArray(arr: any): arr is any[] {
  return Array.isArray(arr) && arr.length > 0;
}

/**
 * 判断是否为有效时间
 */
export function isValidDate(date: Date) {
  return date instanceof Date && !isNaN(date.getTime());
}

/**
 * 获取银行卡背景
 */
export function getBankcardBackground(bankCode?: string) {
  if (!bankCode) {
    return 'background: linear-gradient(to bottom, #fda7a4, #fd524c) !important;border: 1px solid #fde5e4;';
  }

  if (['ABC', 'CMBC', 'PSBC', 'SXCCB', 'ZJNX'].includes(bankCode)) {
    return 'background: linear-gradient(to bottom, #95e6b7, #39bf71) !important;border: 1px solid #e6fff0;';
  }

  if (['HZB', 'CCB', 'BCM', 'JSYH', 'SPDB', 'SHB', 'TJB', 'CIB', 'CEB'].includes(bankCode)) {
    return 'background: linear-gradient(to bottom, #a6cbff, #4c95fc) !important;border: 1px solid #e6f0ff;';
  }

  return 'background: linear-gradient(to bottom, #fda7a4, #fd524c) !important;border: 1px solid #fde5e4;';
}

/**
 * 图片加载器
 * @param url
 */
export function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      resolve(image);
    };

    image.onerror = () => {
      reject(new Error('图片加载失败'));
    };

    image.src = url;
  });
}

export function onMountedOrActivated(hook: () => any) {
  let mounted: boolean;

  onMounted(() => {
    hook();
    nextTick(() => {
      mounted = true;
    });
  });

  onActivated(() => {
    if (mounted) {
      hook();
    }
  });
}

export function getFileType(url: string) {
  const ext = url.substring(url.lastIndexOf('.') + 1).toLowerCase();
  if (['mp4'].includes(ext)) {
    return 'video';
  }
  if (['mp3'].includes(ext)) {
    return 'audio';
  }
  if (['png', 'jpg', 'jpeg'].includes(ext)) {
    return 'image';
  }
  return '';
}

/**
 * 转换卡券数据格式
 */
export function adaptCoupon(data: any) {
  if (data == null) return null;

  let value = '';

  switch (data.type) {
    case CouponTypes.LOAN_DISCOUNT:
    case CouponTypes.FENQI_ZHEKOU_COUPON:
      value = formatDiscount(data.couponValue);
      break;
    case CouponTypes.ZERO_INTEREST_LOAN_COUPON:
      value = String(data.couponValue);
      break;
    case CouponTypes.ANYTIME_REPAY_COUPON:
      value = '限时';
      break;
    case CouponTypes.PRICING_COUPON:
      value = data.couponValueDesc;
      break;
    default:
      value = formatAmount(data.couponValue, '0');
  }

  return {
    id: data.id,
    name: data.typeDescription,
    description: data.description,
    type: data.type,
    startAt: data.effectiveTimeStart,
    endAt: data.effectiveTimeEnd,
    isVip: data.channel === CouponSourceTypes.VIP_CARD,
    value,
    isComfortCouponPack: data.channel === CouponSourceTypes.COMFORT_COUPON_PACK,
    overAmount: data.overAmount,
    unavailableReason: data.unavailableReason,
    slogan: data.slogan,
    couponId: data.couponId,
    typeDescription: data.typeDescription,
    couponValue: data.couponValue,
    benefitText: data.benefitText,
  };
}

/**
 * 将 yyyy-mm-dd 格式转换为 m月d日 格式
 */
export function formatDateToMonthDay(dateStr: string) {
  if (!dateStr) return '';
  const date = dayjs(dateStr);
  if (!date.isValid()) return dateStr;
  return `${date.month() + 1}月${date.date()}日`;
}

/**
 * iframe 使用跳转链接域名获取当前页面路径
 */
export function getIframeCurrentDomain() {
  const domain = getCurrentDomain();
  return process.env.VUE_APP_RUNTIME_ENV === 'prod' || process.env.VUE_APP_RUNTIME_ENV === 'staging'
    ? YXH_OPEN_CUSTOM_DOMAIN
    : domain;
}

/** 域名 → 备案号 映射（为「宜享花域名」的子集，仅这些域名展示移动金融客户端备案信息） */
const FILING_RECORD_MAP: Record<string, string> = {
  'www.yxh1.cn': '琼ICP备18003305号-5',
  'www.yxh3.cn': '琼ICP备18003305号-3',
};

/** 获取当前站点需展示的备案号（无需展示时返回空字符串） */
export function getFilingRecord() {
  return FILING_RECORD_MAP[window.location.hostname] ?? '';
}
